<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="data"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      :items-per-page="24"
      class="elevation-1 text-overflow my-10"
      @click:row="previewItem"
    >
      <template v-slot:top>
        <v-toolbar flat height="70">
          <v-toolbar-title class="headline" :data-cy="title">
            <span>
              {{ title }}
            </span>
            <v-btn
              v-if="canUseKnowledgeBaseBtn"
              @click="handleHelp"
              icon
              color="primary"
            >
              <v-icon>mdi-help-circle</v-icon>
            </v-btn>
          </v-toolbar-title>
          <v-spacer />
          <v-form @submit.prevent="submitSearch">
            <v-text-field
              class="mx-4"
              v-model="query.search"
              :label="$t('components.lists.tasksList.search')"
              single-line
              hide-details
              color="primary"
              @click:append-outer="submitSearch"
              append-outer-icon="mdi-magnify"
            />
          </v-form>
          <v-spacer />
          <slot name="select-headers"></slot>
          <v-btn
            color="primary"
            dark
            text
            data-cy="builderAddNewBtn"
            @click="newItem()"
            >{{ $t("components.lists.tasksList.addNew") }}
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.uuid="{ item }">
        <span>{{ item.uuid.slice(28, item.uuid.length) }} </span>
      </template>

      <template v-slot:item.source="{ item }">
        <span class="text-capitalize"
          >{{ lookupForTranslations(item.source.toLowerCase()) }}
        </span>
      </template>
      <template v-slot:item.description="{ item }">
        <v-tooltip
          bottom
          v-if="item.description && item.description.length !== 0"
        >
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.description.slice(0, 50).concat("...") }}
            </span>
          </template>
          <p style="max-width: 500px" class="text-justify">
            {{ item.description }}
          </p>
        </v-tooltip>
      </template>
      <template v-slot:item.created="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="text-capitalize"
              >{{ formatCreated(item.created, true) }}
            </span>
          </template>
          <span style="display: block; max-width: 400px"
            >{{ $t("components.lists.tasksList.created") }}
            {{ formatCreated(item.created, false) }}</span
          >
        </v-tooltip>
      </template>
      <template v-slot:item.due_date="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="text-capitalize"
              >{{ formatCreated(item.created, true) }}
            </span>
          </template>
          <span style="display: block; max-width: 400px"
            >Due Date: {{ formatCreated(item.created, false) }}</span
          >
        </v-tooltip>
      </template>
      <template v-slot:item.status="{ item }">
        <div v-if="item.status === 'COMPLETED'">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-icon color="primary">{{
                  getPercentageOfCompletionIcon(item).icon
                }}</v-icon>
              </span>
            </template>
            <span>
              {{ $t("components.lists.tasksList.completed") }}
            </span>
          </v-tooltip>
        </div>
        <div v-else-if="item.status === 'NOT_STARTED'">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-icon color="primary">{{
                  getPercentageOfCompletionIcon(item).icon
                }}</v-icon>
              </span>
            </template>
            <span>
              {{ $t("components.lists.tasksList.notStarted") }}
            </span>
          </v-tooltip>
        </div>
        <div v-else>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-icon color="primary">{{
                  getPercentageOfCompletionIcon(item).icon
                }}</v-icon>
              </span>
            </template>
            <span v-if="getPercentageOfCompletion(item) !== null">
              {{ $t("components.lists.tasksList.inProgress") }}:
              {{ getPercentageOfCompletion(item) }} %
            </span>
            <span v-else>
              {{ $t("components.lists.tasksList.cannotBeCalculated") }}
            </span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.crm_boat="{ item }">
        <!-- <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-chip
                class="ma-1 short--chip"
                :key="index"
                v-for="(boatName, index) in formatBoatNames(item.crm_boats)"
              >
                <span>{{ boatName }}</span>
              </v-chip>
            </span>
          </template> -->
        <v-chip
          v-for="(boatName, index) in formatBoatNames(item.crm_boats)"
          :key="index"
          class="ma-1"
        >
          {{ boatName }}
        </v-chip>
        <!-- </v-tooltip> -->
      </template>
      <template v-slot:item.assignee="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-chip
                class="ma-1 short--chip"
                :key="index"
                v-for="(name, index) in formatAssigneeNames(item)"
              >
                <span class="text-center align-center">{{ name }}</span>
              </v-chip>
            </span>
          </template>
          <span v-for="(name, index) in formatAssigneeNames(item)" :key="index">
            {{ name }}
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.priority="{ item }">
        <v-rating v-model="item.priority" length="1" readonly>
          <template v-slot:item="props">
            <v-icon
              class="pa-0 ma-0"
              :color="props.isFilled ? 'primary' : ''"
              v-text="`mdi-numeric-${props.value}-box`"
            ></v-icon>
          </template>
        </v-rating>
      </template>
    </v-data-table>
    <v-pagination
      v-model="currentPage"
      :length="numberOfPages"
      :total-visible="7"
      color="primary"
    />
    <help-dialog
      :dialog="helpDialog"
      @close="closeHelpDialog"
      v-if="helpDialog.active"
    ></help-dialog>
  </div>
</template>

<script>
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";

export default {
  props: {
    title: String,
    headers: Array,
    data: Array,
    sort: String,
    loading: Boolean,
    response: Object,
  },

  components: {
    HelpDialog,
  },

  data: () => ({
    canUseKnowledgeBaseBtn: false,
    helpDialog: { location: null, active: false },
    options: { sortBy: [], sortDesc: [], page: [], itemsPerPage: 24 },
    query: {
      page: 1,
      search: "",
      ordering: "",
    },
  }),
  created() {
    this.query.search = this.$route.query.search || "";
    this.query.page = Number(this.$route.query.page) || 1;
    this.query.ordering = this.$route.query.ordering || "";

    this.canUseKnowledgeBaseBtn = constraints.canUseKnowledgeBaseBtn("tasks");
  },

  watch: {
    options: {
      handler(value) {
        const { sortBy, sortDesc, page, itemsPerPage } = this.options;
        if (sortBy.length !== 0 && sortDesc.length !== 0) {
          const header = this.headers.find(
            (header) => header.value === sortBy[0]
          );
          if (header == null) return;

          this.query = {
            ...this.query,
            ordering: `${sortDesc[0] === true ? "-" : ""}${
              header.orderingName
            }`,
          };
          this.$emit("sort");
        }
      },
      deep: true,
    },
  },

  computed: {
    formTitle() {
      return this.title.substring(0, this.title.length - 1);
    },
    numberOfPages() {
      if (!this.response) {
        return 0;
      }
      return parseInt(Math.ceil(this.response.count / 24));
    },
    currentPage: {
      get: function () {
        return this.query.page;
      },
      set: function (value) {
        this.query = {
          ...this.query,
          page: value,
        };
        this.$emit("reload");
      },
    },
  },

  methods: {
    lookupForTranslations(source) {
      const translateSourceLookup = [
        {
          name: "Team",
          id: "team",
          path: "components.lists.tasksList.team",
        },
        {
          name: "Customer",
          id: "customer",
          path: "components.lists.tasksList.customer",
        },
        {
          name: "Scheduled",
          id: "scheduled",
          path: "components.lists.tasksList.scheduled",
        },
      ];

      const translatedSource = translateSourceLookup.find((item) => {
        return item.id === source;
      });

      if (typeof translatedSource === "undefined") {
        return source;
      }

      return this.$t(translatedSource.path);
    },
    formatAssigneeNames(item) {
      let assignee = item.checklists
        .map((element) => {
          return element.assignee.email;
        })
        .filter((element) => {
          return typeof element !== "undefined";
        });

      return [...new Set(assignee)];
    },
    formatBoatNames(boats) {
      return boats
        .filter((boat) => !!boat.pretty_name)
        .map((boat) => boat.pretty_name);
    },
    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },
    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location = "tasks";
    },
    getPercentageOfCompletion(item) {
      if (item.all_subtasks) {
        return Math.round((item.done_subtasks / item.all_subtasks) * 100);
      }

      let done = item.checklists.reduce((acc, next) => {
        return acc + next.done_subtasks;
      }, 0);

      let all = item.checklists.reduce((acc, next) => {
        return acc + next.all_subtasks;
      }, 0);

      if (all === 0 && done === 0) {
        return 0;
      }

      let progress = Math.round((done * 100) / all);

      if (isNaN(progress)) {
        return null;
      }

      return progress;
    },

    getPercentageOfCompletionIcon(item) {
      const iconLookupTableNotStarted = {
        icon: "mdi-circle-outline",
        value: 0,
      };

      const iconLookupTableInProgress = [
        { icon: "mdi-circle-slice-1", value: 0 },
        { icon: "mdi-circle-slice-2", value: 25 },
        { icon: "mdi-circle-slice-4", value: 50 },
        { icon: "mdi-circle-slice-6", value: 75 },
        { icon: "mdi-circle-slice-7", value: 100 },
      ];

      const iconLookupTableCompleted = {
        icon: "mdi-circle-slice-8",
        value: 100,
      };

      const percentage = this.getPercentageOfCompletion(item);

      if (isNaN(percentage)) {
        if (item.status === "NOT_STARTED") return iconLookupTableNotStarted;
        if (item.status === "IN_PROGRESS") return iconLookupTableInProgress[0];
        if (item.status === "COMPLETED") return iconLookupTableCompleted;
      }

      if (item.status === "NOT_STARTED") {
        return iconLookupTableNotStarted;
      }

      if (item.status === "IN_PROGRESS") {
        return iconLookupTableInProgress.reduce((previous, current) => {
          if (previous.value < percentage && current.value >= percentage) {
            return current;
          } else {
            return previous;
          }
        }, iconLookupTableInProgress[0]);
      }

      if (item.status === "COMPLETED") {
        return iconLookupTableCompleted;
      }
    },

    formatCreated(date, shortDate) {
      if (!date) return;
      if (shortDate) {
        return this.$moment(date).format("MMM D");
      }

      return this.$moment(date).format("YYYY-MM-DD");
    },

    formatDueDate(date, shortDate) {
      if (!date) return;
      if (shortDate) {
        return this.$moment(date).format("MMM D");
      }

      return this.$moment(date).format("YYYY-MM-DD");
    },
    setPage(page) {
      //NOTE: This function is called from the outside
      this.query.page = page;
    },
    submitSearch(event) {
      this.query.page = 1;
      this.$emit("reload");
      return false;
    },
    editItem(item) {
      this.$emit("edit", item);
    },
    deleteItem(item) {
      this.$emit("delete", item);
    },
    previewItem(item) {
      this.$emit("details", item);
    },
    newItem() {
      this.$emit("new", {});
    },
  },
};
</script>

<style lang="scss">
.delete--hover {
  &:hover {
    color: red;
  }
}

.preview--hover {
  &:hover {
    color: --secondary;
  }
}

.edit--hover {
  &:hover {
    color: --primary;
  }
}

// TODO make this width responsible https://css-tricks.com/flexbox-truncated-text/
.short--chip {
  max-width: 150px;
  //display: flex;
  // align-items: center;
  // padding: 10px;
  // margin: 30px 0;
}
.short--chip span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
